import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import message from 'src/components/Message';
import {
  acsIntegrationClient,
  PdkExportCandidate,
  PdkExportResult,
  PdkExportRow,
} from 'src/apis/acsIntegrationClient';

type Props = {
  open: boolean;
  integrationId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
};

type Editable = PdkExportCandidate & {selected: boolean};

const STEPS = ['Review users', 'Confirm', 'Send to PDK'];

export function ExportUsersToPdkWizard({open, integrationId, onClose, onCompleted}: Props) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [residents, setResidents] = useState<Editable[]>([]);
  const [orgUsers, setOrgUsers] = useState<Editable[]>([]);
  const [tab, setTab] = useState<'residents' | 'orgUsers'>('residents');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PdkExportResult | null>(null);

  const load = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    try {
      const res = await acsIntegrationClient.listExportCandidates(integrationId);
      const data = res.data.data;
      setResidents(
        (data.residents || []).map(c => ({...c, selected: !c.alreadyInPdk}))
      );
      setOrgUsers(
        (data.orgUsers || []).map(c => ({...c, selected: !c.alreadyInPdk}))
      );
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}}};
      message.error(err?.response?.data?.message || 'Failed to load Entrio users');
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setResult(null);
    setTab('residents');
    void load();
  }, [open, load]);

  const allSelected = useMemo(
    () => [...residents, ...orgUsers].filter(r => r.selected),
    [residents, orgUsers]
  );

  const toggleAll = (kind: 'residents' | 'orgUsers', value: boolean) => {
    if (kind === 'residents') {
      setResidents(prev =>
        prev.map(r => (r.alreadyInPdk ? r : {...r, selected: value}))
      );
    } else {
      setOrgUsers(prev =>
        prev.map(r => (r.alreadyInPdk ? r : {...r, selected: value}))
      );
    }
  };

  const toggleOne = (kind: 'residents' | 'orgUsers', sourceId: string) => {
    const update = (rows: Editable[]) =>
      rows.map(r =>
        r.sourceId === sourceId && !r.alreadyInPdk ? {...r, selected: !r.selected} : r
      );
    if (kind === 'residents') setResidents(prev => update(prev));
    else setOrgUsers(prev => update(prev));
  };

  const handleSubmit = async () => {
    if (!integrationId) return;
    setSubmitting(true);
    try {
      const payload: PdkExportRow[] = allSelected.map(r => ({
        source: r.source,
        sourceId: r.sourceId,
        entrioUserId: r.entrioUserId,
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        email: r.email.trim(),
      }));
      const res = await acsIntegrationClient.exportUsersToPdk(integrationId, payload);
      setResult(res.data.data);
      const sent = res.data.data?.success?.length || 0;
      const failed = res.data.data?.errors?.length || 0;
      const unlinked = res.data.data?.linkSkipped?.length || 0;
      if (failed === 0 && unlinked === 0) {
        message.success(`Sent ${sent} user(s) to PDK`);
      } else {
        message.warning(
          `Sent ${sent}, ${failed} skipped, ${unlinked} unlinked — see details`
        );
      }
      onCompleted?.();
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}}};
      message.error(err?.response?.data?.message || 'Export failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep0 = () => {
    const list = tab === 'residents' ? residents : orgUsers;
    const selectableTotal = list.filter(r => !r.alreadyInPdk).length;
    const selectedNum = list.filter(r => r.selected).length;
    return (
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          These are the Entrio people who can be sent to PDK. Users already in PDK (matched by
          link record or by email) are grayed out.
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{mb: 1}}>
          <Tab label={`Residents (${residents.length})`} value="residents" />
          <Tab label={`Org users (${orgUsers.length})`} value="orgUsers" />
        </Tabs>
        {loading ? (
          <Stack alignItems="center" sx={{py: 4}}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <TableContainer sx={{maxHeight: 480}}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedNum > 0 && selectedNum < selectableTotal}
                      checked={selectableTotal > 0 && selectedNum === selectableTotal}
                      onChange={e => toggleAll(tab, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {tab === 'residents' ? (
                    <TableCell>Location</TableCell>
                  ) : (
                    <TableCell>Roles</TableCell>
                  )}
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map(r => (
                  <TableRow
                    key={`${r.source}-${r.sourceId}`}
                    sx={{
                      opacity: r.alreadyInPdk ? 0.5 : 1,
                      bgcolor: r.alreadyInPdk ? 'action.hover' : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={r.alreadyInPdk}
                        checked={r.selected}
                        onChange={() => toggleOne(tab, r.sourceId)}
                      />
                    </TableCell>
                    <TableCell>
                      {r.firstName} {r.lastName}
                    </TableCell>
                    <TableCell>{r.email}</TableCell>
                    {tab === 'residents' ? (
                      <TableCell>{r.location || '—'}</TableCell>
                    ) : (
                      <TableCell>
                        {(r.roles || []).map(role => (
                          <Chip key={role} size="small" label={role} sx={{mr: 0.5}} />
                        ))}
                      </TableCell>
                    )}
                    <TableCell>
                      {r.alreadyInPdk ? (
                        <Chip
                          size="small"
                          label={
                            r.linkSource === 'EMAIL_MATCH'
                              ? 'Email in PDK'
                              : 'Already in PDK'
                          }
                        />
                      ) : (
                        <Chip size="small" label="New" color="success" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="body2" color="text.secondary">
          {allSelected.length} selected of {residents.length + orgUsers.length} total
        </Typography>
      </Stack>
    );
  };

  const renderStep1 = () => (
    <Stack spacing={2}>
      <Typography variant="body2">
        About to send <strong>{allSelected.length}</strong> user
        {allSelected.length === 1 ? '' : 's'} to PDK as holders.
      </Typography>
      <Alert severity="info">
        We&apos;ll send <strong>First Name</strong>, <strong>Last Name</strong>, and{' '}
        <strong>Email</strong> for each user. PDK stores the email on the holder, so digital
        credential invites issued later can email the resident directly. Other PDK holder
        fields (Cards, Groups, Pin, Active/Expire dates, etc.) stay untouched and can be set
        in pdk.io afterward.
      </Alert>
      <Box>
        <Typography variant="subtitle2">Breakdown</Typography>
        <Stack direction="row" spacing={2} sx={{mt: 1}}>
          <Chip
            label={`Residents: ${residents.filter(r => r.selected).length}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Org users: ${orgUsers.filter(r => r.selected).length}`}
            color="secondary"
            variant="outlined"
          />
        </Stack>
      </Box>
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={2}>
      {!result ? (
        <>
          <Typography variant="body2">
            Sending <strong>{allSelected.length}</strong> user
            {allSelected.length === 1 ? '' : 's'} to PDK now.
          </Typography>
          {submitting && <LinearProgress />}
        </>
      ) : (
        <>
          <Alert
            severity={
              result.errors.length === 0 && result.linkSkipped.length === 0
                ? 'success'
                : 'warning'
            }
          >
            {result.success.length} sent to PDK
            {result.errors.length > 0 ? `, ${result.errors.length} skipped` : ''}
            {result.linkSkipped.length > 0
              ? `, ${result.linkSkipped.length} unlinked`
              : ''}
            .
          </Alert>
          <Divider />
          {result.errors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Skipped (validation)
              </Typography>
              <TableContainer sx={{maxHeight: 200}}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.row}</TableCell>
                        <TableCell>{e.source || '—'}</TableCell>
                        <TableCell>{e.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {result.linkSkipped.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Unlinked (created in PDK, but couldn&apos;t be matched back)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                These holders were sent to PDK successfully, but we couldn&apos;t uniquely match
                the new PDK holder back to the Entrio user (most often: duplicate names). The
                holders exist in PDK; you can manually link them later or re-run the wizard
                after editing the names.
              </Typography>
              <TableContainer sx={{maxHeight: 200, mt: 1}}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.linkSkipped.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell>{s.row}</TableCell>
                        <TableCell>{s.source}</TableCell>
                        <TableCell>{s.email || '—'}</TableCell>
                        <TableCell>{s.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{pr: 6}}>
        Export Users to PDK
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          sx={{position: 'absolute', right: 8, top: 8}}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{mb: 3}}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </DialogContent>
      <DialogActions>
        {step > 0 && !result && (
          <Button onClick={() => setStep(s => s - 1)} disabled={submitting}>
            Back
          </Button>
        )}
        {step === 0 && (
          <Button
            variant="contained"
            disabled={allSelected.length === 0}
            onClick={() => setStep(1)}
          >
            Next
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" onClick={() => setStep(2)}>
            Next
          </Button>
        )}
        {step === 2 && !result && (
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={submitting || allSelected.length === 0}
          >
            {submitting ? 'Sending…' : `Send ${allSelected.length} to PDK`}
          </Button>
        )}
        {step === 2 && result && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ExportUsersToPdkWizard;
